import { useState, useEffect } from "react";
import {
  ArrowRight,
  CheckCircle,
  ChevronDown,
  Copy,
  Download,
  Edit,
  ExternalLink,
  FileText,
  Search,
  TrendingUp,
  RefreshCw,
  Check,
  Sparkles,
  ArrowUp,
  Plus,
  ChevronRight,
  Eye,
  Save,
} from "lucide-react";

// Main application component
export default function RankFlow() {
  const [productInfo, setProductInfo] = useState({
    name: "FeedbackbyAI",
    website: "https://feedbackby.ai",
    description: "AI-powered feedback on business ideas and marketing content",
    audience: "Founders, marketers, small business owners",
    category: "AI marketing tool",
  });
  const [setupComplete, setSetupComplete] = useState(false);
  const [currentView, setCurrentView] = useState("dashboard"); // dashboard, topicSuggestion, blogEditor
  const [selectedTopic, setSelectedTopic] = useState(null);

  // Updated with only 1 high-quality topic suggestion at a time
  const [topicSuggestion, setTopicSuggestion] = useState({
    id: 1,
    title: "Why AI CRMs Are Saving Solo Freelancers 10 Hours a Week",
    keyword: "freelancer crm tools",
    searchVolume: 1300,
    discussions: 42,
    competition: "Low",
    blogType: "Product Review + Thought Leadership",
    targetWordCount: "1,800 words",
    difficulty: "Medium",
  });

  const [blogHistory, setBlogHistory] = useState([
    {
      id: 1,
      title: "Why AI CRMs Are Saving Solo Freelancers 10 Hours a Week",
      keyword: "freelancer crm tools",
      date: "2 days ago",
      previousRank: 41,
      currentRank: 28,
      improvement: 13,
      wordCount: 1832,
      markup: "<h1>Why AI CRMs Are Saving Solo Freelancers 10 Hours a Week</h1><p>For freelancers juggling multiple clients...</p>",
    },
  ]);

  const handleProductSetup = (productData) => {
    setProductInfo(productData);
    setSetupComplete(true);
  };

  const selectTopic = () => {
    setSelectedTopic(topicSuggestion);
    setCurrentView("blogEditor");
  };

  const generateNewPost = () => {
    setCurrentView("topicSuggestion");
  };

  const handleBack = () => {
    setCurrentView("dashboard");
    setSelectedTopic(null);
  };

  const saveBlog = (blogData) => {
    // Add the new blog to history
    setBlogHistory((prev) => [blogData, ...prev]);
    // Return to dashboard
    setCurrentView("dashboard");
  };

  // Function to refresh topic suggestion (simulated)
  const refreshTopicSuggestion = () => {
    // In a real app, this would call an API to get a new suggestion
    // For now, let's just simulate a new topic suggestion

    // Create an array of high-quality topic suggestions
    const topicOptions = [
      {
        id: 1,
        title: "Why AI CRMs Are Saving Solo Freelancers 10 Hours a Week",
        keyword: "freelancer crm tools",
        searchVolume: 1300,
        discussions: 42,
        competition: "Low",
        blogType: "Product Review + Thought Leadership",
        targetWordCount: "1,800 words",
        difficulty: "Medium",
      },
      {
        id: 2,
        title: "The Complete Guide to Enterprise AI Implementation in 2025",
        keyword: "enterprise ai implementation guide",
        searchVolume: 2400,
        discussions: 65,
        competition: "Medium",
        blogType: "Comprehensive Guide",
        targetWordCount: "2,500 words",
        difficulty: "High",
      },
      {
        id: 3,
        title: "How SaaS Founders Are Using Feedback Loops to Increase Retention by 37%",
        keyword: "saas feedback loops retention",
        searchVolume: 950,
        discussions: 29,
        competition: "Low",
        blogType: "Case Study + How-To",
        targetWordCount: "1,650 words",
        difficulty: "Medium",
      },
    ];

    // Pick a different topic than the current one
    const currentTopicId = topicSuggestion.id;
    let filteredOptions = topicOptions.filter((topic) => topic.id !== currentTopicId);
    const randomIndex = Math.floor(Math.random() * filteredOptions.length);

    setTopicSuggestion(filteredOptions[randomIndex]);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="h-8 w-8 bg-green-600 rounded-md flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <span className="ml-2 text-xl font-bold text-gray-800">RankFlow</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-sm text-gray-500 hover:text-gray-700">Help</button>
              <div className="relative">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-semibold">JD</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!setupComplete ? (
          <WelcomeAndSetup onProductSetup={handleProductSetup} />
        ) : (
          <div className="space-y-6">
            {/* Always visible product info section */}
            <ProductInfoSection productInfo={productInfo} />

            {/* Conditional view based on current state */}
            {currentView === "dashboard" && <DashboardContent onGenerateNew={generateNewPost} blogHistory={blogHistory} />}

            {currentView === "topicSuggestion" && (
              <TopicSuggestionView
                topic={topicSuggestion}
                onSelectTopic={selectTopic}
                onBack={handleBack}
                onRefresh={refreshTopicSuggestion}
              />
            )}

            {currentView === "blogEditor" && selectedTopic && (
              <BlogEditorView topic={selectedTopic} onBack={handleBack} onSave={saveBlog} productInfo={productInfo} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Welcome view and product setup combined
function WelcomeAndSetup({ onProductSetup }) {
  const [productData, setProductData] = useState({
    name: "",
    website: "",
    description: "",
    audience: "",
    category: "",
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setProductData((prev) => ({
      ...prev,
      [id.replace("product-", "")]: value,
    }));
  };

  const handleSubmit = () => {
    onProductSetup(productData);
  };

  return (
    <div className="space-y-10">
      <div className="text-center py-8">
        <div className="h-20 w-20 bg-green-50 rounded-full flex items-center justify-center mx-auto">
          <TrendingUp className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="mt-6 text-3xl font-extrabold text-gray-900">Welcome to RankFlow</h1>
        <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">The Self-Improving Blog Post that writes and ranks — automatically</p>
      </div>

      <div className="max-w-xl mx-auto bg-white shadow-sm rounded-xl overflow-hidden">
        <div className="px-6 py-5 bg-green-50 border-b border-green-100">
          <h2 className="text-xl font-bold text-gray-900">Tell us about your product</h2>
          <p className="mt-1 text-sm text-gray-600">We'll use this to find relevant keywords and create tailored content</p>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label htmlFor="product-name" className="block text-sm font-medium text-gray-700">
              Product name
            </label>
            <input
              type="text"
              id="product-name"
              value={productData.name}
              onChange={handleChange}
              placeholder="e.g., FeedbackbyAI"
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="product-website" className="block text-sm font-medium text-gray-700">
              Website or landing page URL
            </label>
            <input
              type="url"
              id="product-website"
              value={productData.website}
              onChange={handleChange}
              placeholder="e.g., https://feedbackby.ai"
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="product-description" className="block text-sm font-medium text-gray-700">
              Short product description (what it does)
            </label>
            <textarea
              id="product-description"
              rows={3}
              value={productData.description}
              onChange={handleChange}
              placeholder="e.g., An AI tool that provides feedback on business ideas and marketing content"
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="product-audience" className="block text-sm font-medium text-gray-700">
              Target audience
            </label>
            <input
              type="text"
              id="product-audience"
              value={productData.audience}
              onChange={handleChange}
              placeholder="e.g., founders, marketers, small business owners"
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="product-category" className="block text-sm font-medium text-gray-700">
              Category/industry
            </label>
            <input
              type="text"
              id="product-category"
              value={productData.category}
              onChange={handleChange}
              placeholder="e.g., AI marketing tool, business analytics"
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="pt-4">
            <button
              onClick={handleSubmit}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Always visible product info section
function ProductInfoSection({ productInfo }) {
  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden">
      <div className="px-4 py-4 sm:px-6 flex justify-between items-center border-b border-gray-100">
        <h3 className="font-medium text-gray-900 flex items-center">
          <div className="h-6 w-6 bg-green-100 rounded-md flex items-center justify-center mr-2">
            <FileText className="h-3.5 w-3.5 text-green-600" />
          </div>
          Product Information
        </h3>
        <button className="text-sm text-green-600 hover:text-green-800 flex items-center">
          <Edit className="h-3.5 w-3.5 mr-1" />
          Edit
        </button>
      </div>

      <div className="px-4 py-4 sm:px-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
        <div>
          <span className="text-gray-500 text-xs">Product name</span>
          <span className="block font-medium mt-0.5">{productInfo.name}</span>
        </div>

        <div>
          <span className="text-gray-500 text-xs">Website</span>
          <a
            href={productInfo.website}
            target="_blank"
            rel="noopener noreferrer"
            className="block font-medium mt-0.5 text-green-600 hover:text-green-800 truncate"
          >
            {productInfo.website}
          </a>
        </div>

        <div className="col-span-1 sm:col-span-2">
          <span className="text-gray-500 text-xs">Description</span>
          <span className="block font-medium mt-0.5">{productInfo.description}</span>
        </div>

        <div>
          <span className="text-gray-500 text-xs">Target audience</span>
          <span className="block font-medium mt-0.5">{productInfo.audience}</span>
        </div>

        <div>
          <span className="text-gray-500 text-xs">Category</span>
          <span className="block font-medium mt-0.5">{productInfo.category}</span>
        </div>
      </div>
    </div>
  );
}

// Dashboard content view
function DashboardContent({ onGenerateNew, blogHistory }) {
  return (
    <div className="space-y-6">
      {/* Generate new content button */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <div className="p-8 flex flex-col items-center">
          <div className="h-16 w-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
            <Sparkles className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Find This Week's SEO Opportunity</h2>
          <p className="text-gray-600 text-center mb-6 max-w-xl">
            Discover trending topics matched to high-value keywords specific to your product
          </p>
          <button
            onClick={onGenerateNew}
            className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
          >
            <Search className="mr-2 h-4 w-4" />
            Find New Opportunities
          </button>
        </div>
      </div>

      {/* Blog history section */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center">
            <div className="h-6 w-6 bg-green-100 rounded-md flex items-center justify-center mr-2">
              <FileText className="h-3.5 w-3.5 text-green-600" />
            </div>
            Your Content History
          </h2>
          <span className="text-sm text-gray-500">{blogHistory.length} posts</span>
        </div>

        <div className="divide-y divide-gray-100">
          {blogHistory.length > 0 ? (
            blogHistory.map((blog) => (
              <div key={blog.id} className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900">{blog.title}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {blog.keyword}
                      </span>
                      <span className="text-gray-500">{blog.date}</span>
                      {blog.wordCount && <span className="text-gray-500">{blog.wordCount.toLocaleString()} words</span>}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-4 sm:gap-6">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Previous rank</span>
                        <span className="text-lg font-semibold text-gray-700">#{blog.previousRank}</span>
                      </div>
                      <div className="flex items-center text-gray-400">
                        <ArrowRight className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Current rank</span>
                        <div className="flex items-center">
                          <span className="text-lg font-semibold text-green-600">#{blog.currentRank}</span>
                          <button className="ml-1.5 p-0.5 rounded-full hover:bg-gray-100">
                            <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Improvement</span>
                        <span className="text-lg font-semibold text-green-600 flex items-center">
                          <ArrowUp className="h-3.5 w-3.5 mr-0.5" />
                          {blog.improvement}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 lg:mt-0 lg:ml-5 flex flex-wrap gap-2">
                    <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                      <Copy className="mr-1.5 h-4 w-4 text-gray-500" />
                      Copy
                    </button>
                    <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                      <Eye className="mr-1.5 h-4 w-4 text-gray-500" />
                      View
                    </button>
                  </div>
                </div>

                {/* Fix suggestions with apply button */}
                <div className="mt-5 bg-green-50 border border-green-100 rounded-lg p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-green-800 flex items-center">
                        <div className="h-5 w-5 bg-green-200 rounded-full flex items-center justify-center mr-1.5">
                          <Plus className="h-3 w-3 text-green-700" />
                        </div>
                        Suggested Fix
                      </h4>
                      <p className="mt-1.5 text-sm text-green-700">
                        Add an FAQ section with 5 questions about "{blog.keyword}" to improve search visibility
                      </p>
                    </div>
                    <button className="mt-4 lg:mt-0 inline-flex items-center px-3.5 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors">
                      <Check className="mr-1.5 h-4 w-4" />
                      Apply This Fix
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-12 text-center">
              <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No content yet</h3>
              <p className="text-gray-500 mb-4">Generate your first blog post to get started</p>
              <button
                onClick={onGenerateNew}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Create New Post
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Single topic suggestion view (instead of a list)
function TopicSuggestionView({ topic, onSelectTopic, onBack, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center mb-4">
        <button onClick={onBack} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors">
          <ChevronDown className="h-4 w-4 mr-1 transform rotate-90" />
          Back to dashboard
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-green-50">
          <h2 className="text-lg font-bold text-gray-900">Your Optimized Content Opportunity</h2>
          <p className="mt-1 text-sm text-gray-600">
            We've analyzed trending conversations and matched them to your product. This topic has high ranking potential.
          </p>
        </div>

        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between">
            <div className="flex-1">
              <div className="flex items-start">
                <div className="h-6 w-6 mt-1 bg-green-100 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                  <FileText className="h-3.5 w-3.5 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{topic.title}</h3>
              </div>

              <div className="mt-2 flex items-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                  {topic.keyword}
                </span>
                <span className="text-sm text-gray-500">
                  Suggested content type:
                  <span className="font-medium ml-1">{topic.blogType}</span>
                </span>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-y-4 sm:gap-y-2 sm:gap-x-4 text-sm">
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-green-50 rounded-full flex items-center justify-center mr-3">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Monthly searches</span>
                    <span className="text-base font-semibold text-gray-900">{topic.searchVolume.toLocaleString()}</span>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="h-10 w-10 bg-green-50 rounded-full flex items-center justify-center mr-3">
                    <ChevronRight className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Discussions</span>
                    <span className="text-base font-semibold text-gray-900">{topic.discussions} this week</span>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="h-10 w-10 bg-green-50 rounded-full flex items-center justify-center mr-3">
                    <ArrowDown className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Competition</span>
                    <span className="text-base font-semibold text-gray-900">{topic.competition}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-start">
                  <Sparkles className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">AI Content Strategy</h4>
                    <p className="mt-1 text-sm text-gray-600">
                      Our AI will create a {topic.targetWordCount} blog with proven structure for the {topic.blogType} format, optimized for
                      SEO and reader engagement.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  onClick={onSelectTopic}
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
                >
                  Generate Blog Post
                </button>

                <button
                  onClick={onRefresh}
                  className="inline-flex items-center justify-center px-5 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Get Another Topic
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// One-step blog editor with full generation
function BlogEditorView({ topic, onBack, onSave, productInfo }) {
  const [generating, setGenerating] = useState(true);
  const [blogContent, setBlogContent] = useState("");
  const [metadata, setMetadata] = useState({
    title: "",
    description: "",
    featuredImage: "/api/placeholder/800/400",
    wordCount: 0,
    readingTime: "",
  });

  // Mock HTML content for the generated blog
  const mockBlogHtml = `
<h1>${topic.title}</h1>

<p>In today's fast-paced digital landscape, freelancers are increasingly turning to specialized CRM tools to streamline their client management processes. These AI-powered solutions are revolutionizing how independent professionals handle their business operations, saving them valuable time and resources.</p>

<h2>The Time-Saving Revolution</h2>

<p>Traditional CRM systems were designed with large enterprises in mind, often requiring extensive setup and maintenance. Modern AI CRMs tailored for freelancers offer a dramatically different experience. With intuitive interfaces and automation capabilities, these tools eliminate much of the administrative burden that solo professionals face.</p>

<p>Key features contributing to time savings include:</p>

<ul>
  <li><strong>Automated client communication</strong> - Schedule and send follow-ups without manual intervention</li>
  <li><strong>Smart contact management</strong> - Organize client information with AI-assisted categorization</li>
  <li><strong>Intelligent proposal generation</strong> - Create customized proposals in minutes rather than hours</li>
  <li><strong>Invoice and payment automation</strong> - Streamline financial processes from billing to receipt</li>
</ul>

<h2>Real-World Impact: 10 Hours Saved Weekly</h2>

<p>Recent surveys of freelancers who implemented AI CRM solutions reported average time savings of approximately 10 hours per week. This significant reduction in administrative work translates to either increased billable hours or improved work-life balance.</p>

<blockquote>
  <p>"Since implementing [AI CRM Tool], I've been able to take on two additional clients without feeling overwhelmed. The automation handles my follow-ups and appointment scheduling, freeing me to focus on delivering quality work."</p>
  <cite>— Sarah Chen, Graphic Designer</cite>
</blockquote>

<h2>Integration with ${productInfo.name}</h2>

<p>${productInfo.description} complements these CRM tools by providing valuable insights that freelancers can incorporate into their client management approach. The combination creates a powerful ecosystem for solo professionals looking to maximize their efficiency.</p>

<h2>Top AI CRMs for Freelancers in 2025</h2>

<ol>
  <li><strong>SoloCRM Pro</strong> - Best for comprehensive client management</li>
  <li><strong>Freelance Flow</strong> - Excellent for creative professionals</li>
  <li><strong>ClientMind AI</strong> - Superior automation capabilities</li>
  <li><strong>Independent Edge</strong> - Great for service-based businesses</li>
</ol>

<h2>Implementation Strategy</h2>

<p>Freelancers looking to reclaim their time should consider the following implementation approach:</p>

<ol>
  <li>Audit current time expenditure on administrative tasks</li>
  <li>Identify specific pain points in client management workflow</li>
  <li>Select a CRM solution that addresses these specific needs</li>
  <li>Invest time upfront in proper setup and data migration</li>
  <li>Establish new workflows that leverage automation features</li>
</ol>

<h2>Conclusion: The Freelancer's New Advantage</h2>

<p>As the freelance economy continues to grow, those who leverage AI-powered tools gain a significant competitive advantage. By reclaiming 10 hours weekly through intelligent CRM systems, solo professionals can focus on what they do best: delivering exceptional work to their clients.</p>

<p>For freelancers ready to transform their practice, the right AI CRM is no longer a luxury—it's an essential investment in sustainable success.</p>

<h2>FAQ: AI CRMs for Freelancers</h2>

<h3>How long does it take to set up an AI CRM system?</h3>
<p>Most freelancer-focused AI CRMs can be set up within 1-2 hours, with full data migration taking up to a day depending on the volume of client information.</p>

<h3>Are AI CRMs affordable for solo freelancers?</h3>
<p>Yes, many solutions offer tiered pricing with starter plans specifically designed for individuals, typically ranging from $15-30 monthly.</p>

<h3>Do I need technical expertise to use these systems?</h3>
<p>Most modern AI CRMs are designed with non-technical users in mind, featuring intuitive interfaces and comprehensive onboarding.</p>

<h3>Can AI CRMs integrate with my existing tools?</h3>
<p>Yes, most offer integration with common freelancer tools like accounting software, calendar applications, and communication platforms.</p>

<h3>How secure are these systems for client data?</h3>
<p>Reputable AI CRMs employ enterprise-grade security measures including encryption, secure authentication, and regular security audits.</p>
`;

  // Show blog generation steps before displaying final content
  const [generationSteps, setGenerationSteps] = useState([
    { step: "Analyzing topic", completed: false },
    { step: "Detecting optimal content type", completed: false },
    { step: "Creating SEO-optimized structure", completed: false },
    { step: "Generating 1,800 words of high-value content", completed: false },
    { step: "Adding keyword optimizations", completed: false },
  ]);

  const [currentStep, setCurrentStep] = useState(0);

  // Simulate blog generation with step-by-step progress
  useEffect(() => {
    if (!generating) return;

    const stepTimers = [];

    // Process each step with a delay
    generationSteps.forEach((step, index) => {
      const timer = setTimeout(() => {
        setCurrentStep(index);
        setGenerationSteps((prev) => prev.map((s, i) => (i === index ? { ...s, completed: true } : s)));

        // When all steps complete, show the final blog
        if (index === generationSteps.length - 1) {
          setTimeout(() => {
            setBlogContent(mockBlogHtml);
            setMetadata({
              title: topic.title,
              description:
                "Learn how freelancers are leveraging AI CRM tools to save time and increase productivity. Discover the best tools and implementation strategies for 2025.",
              featuredImage: "/api/placeholder/800/400",
              wordCount: 1832,
              readingTime: "4 min read",
            });
            setGenerating(false);
          }, 800);
        }
      }, 1000 * (index + 1));

      stepTimers.push(timer);
    });

    return () => stepTimers.forEach((timer) => clearTimeout(timer));
  }, [generating, topic]);

  const handleSaveBlog = () => {
    // Create new blog entry
    const newBlog = {
      id: Date.now(),
      title: metadata.title,
      keyword: topic.keyword,
      date: "Just now",
      previousRank: "-",
      currentRank: "-",
      improvement: 0,
      wordCount: metadata.wordCount,
      markup: blogContent,
    };

    // Pass to parent component
    onSave(newBlog);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors">
          <ChevronDown className="h-4 w-4 mr-1 transform rotate-90" />
          Back to topics
        </button>

        {!generating && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {metadata.wordCount.toLocaleString()} words • {metadata.readingTime}
            </span>
          </div>
        )}
      </div>

      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-green-50 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                <FileText className="h-3.5 w-3.5 text-green-600" />
              </div>
              {generating ? "Generating Blog Post..." : "Edit Your Blog Post"}
            </h2>
            {!generating && <p className="mt-1 text-sm text-gray-600">Keyword: {topic.keyword}</p>}
          </div>

          {!generating && (
            <div className="flex space-x-2">
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <Edit className="mr-1.5 h-4 w-4 text-gray-500" />
                Edit Metadata
              </button>
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <ExternalLink className="mr-1.5 h-4 w-4 text-gray-500" />
                Preview
              </button>
            </div>
          )}
        </div>

        {generating ? (
          <div className="p-16 flex flex-col items-center justify-center">
            <div className="h-16 w-16 relative mb-8">
              <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
              <div className="absolute inset-0 rounded-full border-4 border-green-500 border-t-transparent animate-spin"></div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-6">Creating Your Blog Post</h3>

            <div className="w-full max-w-md">
              {generationSteps.map((step, index) => (
                <div key={index} className="flex items-center mb-4">
                  <div
                    className={`h-6 w-6 rounded-full flex items-center justify-center mr-3 ${
                      step.completed
                        ? "bg-green-100 text-green-600"
                        : index === currentStep
                        ? "border-2 border-green-500 border-t-transparent animate-spin"
                        : "border border-gray-300"
                    }`}
                  >
                    {step.completed && <Check className="h-4 w-4" />}
                  </div>
                  <span
                    className={`text-sm ${
                      step.completed ? "text-green-600 font-medium" : index === currentStep ? "text-gray-900" : "text-gray-500"
                    }`}
                  >
                    {step.step}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            {/* Blog title & metadata */}
            <div className="p-6 border-b border-gray-100">
              <div className="mb-4">
                <label htmlFor="blog-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Blog Title
                </label>
                <input
                  type="text"
                  id="blog-title"
                  value={metadata.title}
                  onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
                  className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg font-bold"
                />
              </div>

              <div>
                <label htmlFor="blog-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Meta Description
                </label>
                <textarea
                  id="blog-description"
                  value={metadata.description}
                  onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                  rows={2}
                  className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                />
              </div>
            </div>

            {/* Content editor */}
            <div className="p-6">
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: blogContent }}
                contentEditable={true}
                style={{ minHeight: "500px", outline: "none" }}
              />
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
              <div className="flex space-x-2">
                <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                  <Copy className="mr-1.5 h-4 w-4 text-gray-500" />
                  Copy HTML
                </button>
                <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                  <Download className="mr-1.5 h-4 w-4 text-gray-500" />
                  Download
                </button>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={onBack}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={handleSaveBlog}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
                >
                  <Save className="mr-1.5 h-4 w-4" />
                  Save to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ArrowDown icon component
function ArrowDown(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 5v14M19 12l-7 7-7-7" />
    </svg>
  );
}
